import Phaser from "phaser";
import { BAT_RADIUS, type Bat, batPosition } from "../../sim";
import { pixelRatio } from "../display";
import { type Fit, imageToScreen } from "../layout";
import { BAT_GLOW } from "../palette";
import { BAT_TEX_W, GLOW_SIZE, TEX } from "./textures";

/** Its wingspan on screen: the catch area's width, at least this many CSS pixels. */
const MIN_SPAN_PX = 90;

/** The bat crossing the sky: a flapping silhouette with a faint purple glow behind it. */
export class BatView {
	private readonly glow: Phaser.GameObjects.Image;
	private readonly body: Phaser.GameObjects.Image;

	constructor(
		private readonly scene: Phaser.Scene,
		readonly bat: Bat,
	) {
		this.glow = scene.add
			.image(0, 0, TEX.glow)
			.setBlendMode(Phaser.BlendModes.ADD)
			.setTint(BAT_GLOW)
			.setDepth(5);
		this.body = scene.add
			.image(0, 0, TEX.bat)
			.setDepth(6)
			.setFlipX(bat.toX < bat.fromX);
	}

	/** `tick` = the sim tick plus the loop's progress; `time` = ms for the flapping. */
	update(fit: Fit, tick: number, time: number): void {
		const at = batPosition(this.bat, tick);
		const p = imageToScreen(fit, at.x, at.y);
		const span = Math.max(BAT_RADIUS * 2 * fit.scale, MIN_SPAN_PX * pixelRatio());
		const flap = 0.45 + 0.55 * Math.abs(Math.sin(time * 0.022));
		const scale = span / BAT_TEX_W;
		this.body.setPosition(p.x, p.y).setScale(scale, scale * flap);
		this.glow
			.setPosition(p.x, p.y)
			.setScale((span * 1.3) / GLOW_SIZE)
			.setAlpha(0.35);
	}

	/** Flew off: fade out. */
	fadeOut(): void {
		this.scene.tweens.add({
			targets: [this.glow, this.body],
			alpha: 0,
			duration: 250,
			onComplete: () => this.destroy(),
		});
	}

	destroy(): void {
		this.glow.destroy();
		this.body.destroy();
	}
}
